import React from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Sparkles as Squircle, Home as House, Building2 as Buildings, Calendar as CalendarBlank, Flag, CheckSquare as CheckSquareOffset, ListChecks, LogOut as SignOut, Users, CreditCard, Award, Crown, Megaphone, Rocket, Gauge, BookOpen } from "lucide-react";

const NAV = [
  { to: "/dashboard", label: "Overview", icon: House },
  { to: "/launch-day", label: "Launch Day", icon: Rocket, highlight: true },
  { to: "/brands", label: "Brands", icon: Buildings },
  { to: "/approvals", label: "Approval Queue", icon: CheckSquareOffset },
  { to: "/calendar", label: "Calendar", icon: CalendarBlank },
  { to: "/flags", label: "Knowledge Flags", icon: Flag },
  { to: "/strategy", label: "Strategy", icon: BookOpen },
  { to: "/workspaces", label: "Workspaces", icon: Users },
  { to: "/onboarding", label: "Substack Setup", icon: ListChecks },
  { to: "/mogul-mode", label: "Mogul Mode", icon: Crown },
  { to: "/sponsorships", label: "Sponsorships", icon: Megaphone },
  { to: "/billing", label: "Membership", icon: CreditCard },
  { to: "/affiliate", label: "Affiliate", icon: Award },
  { to: "/limits", label: "Limits & Keys", icon: Gauge },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white text-[#0A0A0B]">
      <header className="fixed top-0 left-0 right-0 z-40 bg-white border-b border-[#E2E4E9]">
        <div className="flex items-center justify-between h-16 px-6">
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2"
            data-testid="logo-button"
          >
            <Squircle size={22} />
            <span className="font-display font-black tracking-tighter text-lg">VITALITY/<span className="text-[#002FA7]">SS</span></span>
          </button>
          <div className="flex items-center gap-4">
            <div className="text-eyebrow hidden md:block">{user?.email}</div>
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-8 h-8 rounded-none border border-black" />
            ) : (
              <div className="w-8 h-8 bg-[#F4F5F6] border border-black flex items-center justify-center text-xs mono">{user?.name?.[0] || "U"}</div>
            )}
            <button onClick={logout} data-testid="logout-button" className="text-eyebrow hover:text-[#E11D48] flex items-center gap-1">
              <SignOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <aside className="fixed left-0 top-16 bottom-0 w-56 border-r border-[#E2E4E9] bg-white z-30 overflow-y-auto">
        <nav className="p-3 space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replaceAll(" ", "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 border ${
                  isActive
                    ? "bg-[#0A0A0B] text-white border-[#0A0A0B]"
                    : n.highlight
                      ? "bg-[#FFFBEB] text-[#0A0A0B] border-[#F59E0B] hover:bg-[#F59E0B] hover:text-black"
                      : "bg-white text-[#0A0A0B] border-transparent hover:border-[#0A0A0B]"
                } transition-all`
              }
            >
              <n.icon size={18} />
              <span className="text-sm">{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-4 mt-6 border-t border-[#E2E4E9]">
          <div className="text-eyebrow mb-2">Method</div>
          <p className="text-xs text-[#52525B] leading-relaxed">
            Every post engineered for viral sales using proven hook frameworks. You approve before anything ships.
          </p>
        </div>
      </aside>

      <main className="pl-56 pt-16 min-h-screen">
        <div className="p-6 md:p-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
